import * as React from 'react';
import type { ShortCode } from '@highereducation/wordpress';
import {
  Table,
  TableBody,
  TableRow,
  TableCell,
  TableCaption,
  SectionHeader,
  Text,
  Link,
  Card,
  CardContent,
  Heading,
  Grid,
  Flex,
} from '@highereducation/hen-ui';

// ---------------------------------------------------------------------------
// [hen-ui-comparison-table] — wraps a titled data table or stat-card grid
// ---------------------------------------------------------------------------

type ComparisonTableShortcodeProps = {
  variant?: 'complex' | 'simple';
  title?: string;
  description?: string;
  'source-name'?: string;
  'source-link'?: string;
  'cta-label'?: string;
  'cta-link'?: string;
  children?: React.ReactNode;
  [key: string]: unknown;
};

/** Hen-ui `Grid` only allows fixed column counts 1–6. */
function gridColumnsForSimpleLayout(childCount: number): 1 | 2 | 3 | 4 | 5 | 6 {
  if (childCount <= 0) {
    return 1;
  }
  if (childCount > 3) {
    return 3;
  }
  return childCount as 1 | 2 | 3;
}

export const ComparisonTableShortcode: ShortCode<ComparisonTableShortcodeProps> =
  {
    component: ({
      variant = 'complex',
      title,
      description,
      'source-name': sourceName,
      'source-link': sourceLink,
      'cta-label': ctaLabel,
      'cta-link': ctaLink,
      children,
    }) => {
      const source = sourceName ? (
        <Text variant='b5'>
          Source:{' '}
          {sourceLink ? (
            <Link href={sourceLink}>{sourceName}</Link>
          ) : (
            sourceName
          )}
        </Text>
      ) : null;

      if (variant === 'simple') {
        const childCount = React.Children.count(children);
        const columns = gridColumnsForSimpleLayout(childCount);
        return (
          <Flex direction='column' gap='md'>
            <Grid columns={columns} gap='md'>
              {children}
            </Grid>
            {source}
          </Flex>
        );
      }

      return (
        <Flex direction='column' gap='md'>
          {title && (
            <SectionHeader size='h3' title={title} subtext={description} />
          )}
          <Table>
            {ctaLabel && ctaLink && (
              <TableCaption>
                <Link href={ctaLink}>{ctaLabel}</Link>
              </TableCaption>
            )}
            <TableBody>{children}</TableBody>
          </Table>
          {source}
        </Flex>
      );
    },
  };

// ---------------------------------------------------------------------------
// [hen-ui-comparison-row] — a single row in a comparison table
// ---------------------------------------------------------------------------

type ComparisonRowShortcodeProps = {
  label?: string;
  value?: string;
  'value-link'?: string;
  [key: string]: unknown;
};

export const ComparisonRowShortcode: ShortCode<ComparisonRowShortcodeProps> = {
  component: ({ label, value, 'value-link': valueLink }) => {
    if (!label) return null;
    return (
      <TableRow>
        <TableCell>{label}</TableCell>
        {value && (
          <TableCell>
            {valueLink ? <Link href={valueLink}>{value}</Link> : value}
          </TableCell>
        )}
      </TableRow>
    );
  },
};

// ---------------------------------------------------------------------------
// [hen-ui-comparison-stat] — a stat card for the simple variant
// ---------------------------------------------------------------------------

type ComparisonStatShortcodeProps = {
  label?: string;
  value?: string;
  [key: string]: unknown;
};

export const ComparisonStatShortcode: ShortCode<ComparisonStatShortcodeProps> =
  {
    component: ({ label, value }) => {
      if (!label && !value) return null;
      return (
        <Card variant='background-white' border='strokeLight' rounded='md'>
          <CardContent padding='px-md py-sm' className='gap-0'>
            {label && <Heading variant='h6'>{label}</Heading>}
            {value && (
              <Text variant='b1' weight='bold'>
                {value}
              </Text>
            )}
          </CardContent>
        </Card>
      );
    },
  };
